import { useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import { gql } from '@apollo/client';
import { type AuthMethod } from './useAuthState';
import { isValidPhoneNumber, isValidEmail, getPhoneValidationError } from '../utils';
import { type CountryCode, type LookupFailedInfo, type AccessBlockedInfo, type AuthStep } from '../types';

const SEND_SMS_CODE = gql`
  mutation SendSmsCode($phoneNumber: String!) {
    sendSmsCode(phoneNumber: $phoneNumber)
  }
`;

const VERIFY_SMS_CODE = gql`
  mutation VerifySmsCode($phoneNumber: String!, $code: String!) {
    verifySmsCode(phoneNumber: $phoneNumber, code: $code) {
      token
      accountStatus
      identifier
      client {
        id
        fName
        lName
        email
        phoneNumber
      }
    }
  }
`;

const SEND_EMAIL_CODE = gql`
  mutation SendEmailCode($email: String!) {
    sendEmailCode(email: $email)
  }
`;

const VERIFY_EMAIL_CODE = gql`
  mutation VerifyEmailCode($email: String!, $code: String!) {
    verifyEmailCode(email: $email, code: $code) {
      token
      accountStatus
      identifier
      client {
        id
        fName
        lName
        email
        phoneNumber
      }
    }
  }
`;

interface UseAuthVerificationParams {
  authMethod: AuthMethod;
  phoneNumber: string;
  localPhoneNumber: string;
  email: string;
  code: string;
  selectedCountry: CountryCode;
  setAuthStep: (step: AuthStep) => void;
  setError: (error: string) => void;
  setCode: (code: string) => void;
  setVerifiedUserId: (id: string | null) => void;
  setVerifiedUserData: (data: any) => void;
  setPendingToken: (token: string | null) => void;
  setShowCaptureDetails: (show: boolean) => void;
  setLookupFailedInfo: (info: LookupFailedInfo | null) => void;
  setAccessBlockedInfo: (info: AccessBlockedInfo | null) => void;
  onSuccess: (token: string) => void;
  onClose: () => void;
}

/**
 * Custom hook to manage authentication verification flow
 *
 * Features:
 * - Send SMS/email verification codes
 * - Verify SMS/email codes
 * - Handle user details capture for incomplete profiles
 * - Manage loading states and errors
 *
 * @param params - Authentication verification parameters
 * @returns Verification handlers and loading states
 */
export const useAuthVerification = (params: UseAuthVerificationParams) => {
  const {
    authMethod,
    phoneNumber,
    localPhoneNumber,
    email,
    code,
    selectedCountry,
    setAuthStep,
    setError,
    setCode,
    setVerifiedUserId,
    setVerifiedUserData,
    setPendingToken,
    setShowCaptureDetails,
    setLookupFailedInfo,
    setAccessBlockedInfo,
    onSuccess,
    onClose,
  } = params;

  const toast = useToast();

  // refetchQueries: ['GetClients'] makes the admin clients-list view refresh lastLoginAt
  // immediately when an admin signs in during the same session — fixes the stale "Never"
  // Tom hit on 2026-04-30.
  const [sendSmsCode, { loading: sendingSms }] = useMutation(SEND_SMS_CODE);
  const [verifySmsCode, { loading: verifyingSms }] = useMutation(VERIFY_SMS_CODE, {
    refetchQueries: ['GetClients'],
    awaitRefetchQueries: false,
  });
  const [sendEmailCode, { loading: sendingEmail }] = useMutation(SEND_EMAIL_CODE);
  const [verifyEmailCode, { loading: verifyingEmail }] = useMutation(VERIFY_EMAIL_CODE, {
    refetchQueries: ['GetClients'],
    awaitRefetchQueries: false,
  });

  const handleSendCode = async () => {
    setError('');

    if (authMethod === 'phone') {
      if (!isValidPhoneNumber(localPhoneNumber, selectedCountry)) {
        const errorMsg = getPhoneValidationError(localPhoneNumber, selectedCountry);
        setError(errorMsg);
        return;
      }

      try {
        await sendSmsCode({
          variables: { phoneNumber },
        });
        setAuthStep('verify');
        toast({
          title: 'Code sent',
          description: 'Check your phone for the verification code',
          status: 'success',
          duration: 3000,
        });
      } catch (err: any) {
        setError(err.message || "Couldn't send the SMS just now. Check the number, then try again or use email instead.");
      }
    } else {
      if (!isValidEmail(email)) {
        setError("That doesn't look like a valid email — please check and try again.");
        return;
      }

      try {
        await sendEmailCode({
          variables: { email },
        });
        setAuthStep('verify');
        toast({
          title: 'Code sent',
          description: 'Check your email for the verification code',
          status: 'success',
          duration: 3000,
        });
      } catch (err: any) {
        setError(err.message || "Couldn't send the email just now. Check the address, then try again or use phone instead.");
      }
    }
  };

  const handleVerifyCode = async (codeValue?: string) => {
    setError('');

    // Use the provided code value (from onComplete) or fall back to state
    const verificationCode = codeValue || code;

    if (verificationCode.length !== 4) {
      setError('Please enter both 2-digit numbers from the message.');
      return;
    }

    try {
      if (authMethod === 'phone') {
        console.log('📱 Verifying SMS code:', verificationCode);
        const result = await verifySmsCode({
          variables: {
            phoneNumber,
            code: verificationCode,
          },
        });
        console.log('📱 SMS verification result:', result);
        const payload = result.data?.verifySmsCode;
        const accountStatus = payload?.accountStatus;
        const token = payload?.token;
        const client = payload?.client;
        console.log('📱 accountStatus:', accountStatus);

        // Fail-closed: tenant disables self-signup and the phone has no matching client.
        if (accountStatus === 'LOOKUP_FAILED') {
          console.log('🚫 LOOKUP_FAILED — switching to no-account view');
          setLookupFailedInfo({ identifier: payload?.identifier || phoneNumber, method: 'phone', code: verificationCode });
          setAuthStep('no-account');
          setCode('');
          return;
        }

        // The code was right but sign-in for this account is switched off (terminated
        // employee / admin block). No token was issued — show the blocked card.
        if (accountStatus === 'ACCESS_BLOCKED') {
          console.log('⛔ ACCESS_BLOCKED — switching to access-blocked view');
          setAccessBlockedInfo({ identifier: payload?.identifier || phoneNumber, method: 'phone' });
          setAuthStep('access-blocked');
          setCode('');
          return;
        }

        if (token && client) {
          // Check if we need to capture missing details (CREATED_NEW or thin EXISTING profile)
          const missingName = !client.fName || !client.lName;
          const missingEmail = !client.email;

          if (missingName || missingEmail) {
            console.log('📝 Opening CaptureUserDetailsModal for missing details');
            setVerifiedUserId(client.id);
            setVerifiedUserData(client);
            setPendingToken(token);
            setShowCaptureDetails(true);
          } else {
            console.log('✅ All details present, proceeding with login');
            onSuccess(token);
            onClose();
          }
        } else {
          console.error('❌ Authentication response is missing its token or client');
          throw new Error('No token received');
        }
      } else {
        console.log('📧 Verifying email code:', verificationCode);
        const result = await verifyEmailCode({
          variables: {
            email,
            code: verificationCode,
          },
        });
        console.log('📧 Email verification result:', result);
        const payload = result.data?.verifyEmailCode;
        const accountStatus = payload?.accountStatus;
        const token = payload?.token;
        const client = payload?.client;
        console.log('📧 accountStatus:', accountStatus);

        if (accountStatus === 'LOOKUP_FAILED') {
          console.log('🚫 LOOKUP_FAILED — switching to no-account view');
          setLookupFailedInfo({ identifier: payload?.identifier || email, method: 'email', code: verificationCode });
          setAuthStep('no-account');
          setCode('');
          return;
        }

        if (accountStatus === 'ACCESS_BLOCKED') {
          console.log('⛔ ACCESS_BLOCKED — switching to access-blocked view');
          setAccessBlockedInfo({ identifier: payload?.identifier || email, method: 'email' });
          setAuthStep('access-blocked');
          setCode('');
          return;
        }

        if (token && client) {
          const missingName = !client.fName || !client.lName;
          const missingPhone = !client.phoneNumber;

          if (missingName || missingPhone) {
            console.log('📝 Opening CaptureUserDetailsModal for missing details');
            setVerifiedUserId(client.id);
            setVerifiedUserData(client);
            setPendingToken(token);
            setShowCaptureDetails(true);
          } else {
            console.log('✅ All details present, proceeding with login');
            onSuccess(token);
            onClose();
          }
        } else {
          console.error('❌ Authentication response is missing its token or client');
          throw new Error('No token received');
        }
      }
    } catch (err: any) {
      console.error('❌ Verification error:', err);
      const raw = err?.message || '';
      let friendly = "We couldn't verify that code. Double-check the two numbers and try again, or request a new code.";
      if (/expired/i.test(raw)) {
        friendly = 'That code has expired (codes are valid for 5 minutes). Tap "Resend code" to get a new one.';
      } else if (/invalid/i.test(raw)) {
        friendly = "That code didn't match. Check the two numbers in your message and try again, or request a new code.";
      }
      setError(friendly);
      setCode('');
    }
  };

  const handleResendCode = async () => {
    setError('');
    setCode('');

    try {
      if (authMethod === 'phone') {
        await sendSmsCode({
          variables: { phoneNumber },
        });
      } else {
        await sendEmailCode({
          variables: { email },
        });
      }
      toast({
        title: 'Code resent',
        description: `Check your ${authMethod === 'phone' ? 'phone' : 'email'} for the new code`,
        status: 'success',
        duration: 3000,
      });
    } catch (err: any) {
      setError(err.message || "Couldn't resend the code. Try again, or change your number/email.");
    }
  };

  const loading = sendingSms || verifyingSms || sendingEmail || verifyingEmail;

  return {
    handleSendCode,
    handleVerifyCode,
    handleResendCode,
    loading,
  };
};
